/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import {
  FaMousePointer,
  FaRegEnvelope,
  FaRegEnvelopeOpen,
} from "react-icons/fa";
import { MdReport } from "react-icons/md";
import { useRecoilValue } from "recoil";
import { api } from "../../../api/GeneratedApi";
import { RecipientAtom } from "../../../atoms/RecipientAtom";
import { InternalUserAtom, UserAtom } from "../../../atoms/UserAtom";
import { IEmailType } from "../../../generated/Api";
import { useDefaultAlias } from "../../../hook/useDefaultAlias";
import useRowsPerPage, { ITableName } from "../../../hook/useRowsPerPage";
import { Flex } from "../../../styles/general";
import { IContact } from "../../../types/Contact";
import {
  IContactEmailStatus,
  IContactEmailType,
} from "../../../types/EmailTypes";
import { localStorageGetItem } from "../../../utils/LocalStorage";
import IconBerserkerMail from "../../Icon";
import PreviewEmail from "../../Modal/container/PreviewEmail";
import { useModal } from "../../Modal/Modal";
import SearchInput from "../../SearchInput";
import { ucfirst } from "../../Settings/Tags/TableRow";
import { TableFooter } from "../../StyledTable";
import FilterEmails from "./FilterEmails";

export const SkeletonCard = styled.div`
  background-image: linear-gradient(
    -90deg,
    #f8f8f8 0%,
    #f0f0f0 50%,
    #f8f8f8 100%
  );
  background-size: 400% 400%;
  animation: shimmer 1.5s ease-in-out infinite;
  @keyframes shimmer {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: -135% 0%;
    }
  }
  &.white {
    background-image: linear-gradient(
      -90deg,
      #f8f8f8 0%,
      #f0f0f0 50%,
      #f8f8f8 100%
    );
  }
`;

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  cursor: default;
`;

export const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: default;
  padding: 50px 0px;
`;

const NoteRow = styled.div`
  padding: 20px;
  display: grid;
  grid-template-columns: 240px 180px 1fr;
  grid-column-gap: 20px;
  width: 100%;
  background: #f8f8f8 0% 0% no-repeat padding-box;
  border-radius: 2px;
  margin-bottom: 10px;
  min-height: 80px;

  :last-child {
    margin-bottom: 0px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  height: 100%;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Between = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 40px;
`;

const Circle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: #fff;
  border: 1px solid;
  border-color: #868482;
  height: 40px;
  width: 40px;
  border-radius: 100%;

  flex-shrink: 0;

  &.Opened {
    border-color: #fb972e;
  }

  &.Clicked {
    border-color: #75b85c;
  }

  &.Reported {
    border-color: #c41700;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 10px;

  .title {
    margin-bottom: 5px;
  }

  .info {
    display: flex;
    align-items: center;
    width: 80px;
  }

  .text {
    font-weight: 400;
  }
`;

const Text = styled.div`
  color: #333333;
  font-size: 14px;

  white-space: nowrap;
`;

const SubTitle = styled.div`
  color: #333333;
  font-size: 16px;
  font-weight: 600;

  &.Opened {
    color: #fb972e;
  }

  &.Opened {
    color: #fb972e;
  }

  &.Clicked {
    color: #75b85c;
  }

  &.Reported {
    color: #c41700;
  }
`;

interface SubjectProps {
  isEllipsis: boolean;
}

const Subject = styled.span<SubjectProps>`
  color: #333333;
  font-size: 16px;

  ${(props) =>
    props.isEllipsis &&
    css`
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      display: inline-block;
    `}
`;
const PreviewModalTitle = styled.div`
  font-size: 18px;
`;
const ButtonShow = styled.div`
  color: #333333;
  font-size: 16px;
  font-weight: 600;
  min-width: 90px;
  width: 120px;
  display: inline-block;
  cursor: pointer;
  vertical-align: bottom;

  :hover {
    opacity: 0.7;
  }
`;

const noteTitle = css`
  font-size: 18px;
`;
interface Props {
  contact: IContact;
}

export enum DateFilter {
  Recent = "recent",
  Older = "older",
}
export interface FilterEmail {
  status: IContactEmailStatus[];
  type: IContactEmailType[];
  date: DateFilter;
}

export interface EmailToContact {
  _id: string;
  subject: string;
  scheduleTo: string;
  aliasId: string;
  body: string;
  type: IContactEmailType;
  status: IContactEmailStatus;
  isPersonal?: boolean;
}

export const EMAIL_TYPE_NAME: { [key: string]: string } = {
  [IContactEmailType.CAMPAIGN]: "Campaign",
  [IContactEmailType.BROADCAST]: "Broadcast",
  [IContactEmailType.CONFIRMATION]: "Confirmation",
  [IContactEmailType.PERSONAL]: "Personal Email",
};

const TYPE_MAP: { [key: string]: IContactEmailType } = {
  [IEmailType.Campaign]: IContactEmailType.CAMPAIGN,
  [IEmailType.Cancelled]: IContactEmailType.BROADCAST,
  [IEmailType.Confirmation]: IContactEmailType.CONFIRMATION,
  [IEmailType.Draft]: IContactEmailType.BROADCAST,
  [IEmailType.Schedule]: IContactEmailType.BROADCAST,
  [IEmailType.Sent]: IContactEmailType.BROADCAST,
};
interface Props {
  contact: IContact;
}

export default function Emails(props: Props) {
  const [infoFilter, setInfoFilter] = useState("");
  const [filter, setFilter] = useState<FilterEmail>({
    status: [],
    type: [],
    date: DateFilter.Recent,
  });
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [emails, setEmails] = useState<EmailToContact[]>([]);
  const [filteredEmails, setFilteredEmails] = useState<EmailToContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.EMAILS
  );

  useEffect(() => {
    (async () => {
      const token = localStorageGetItem("USER_TOKEN");
      const newEmails = (
        await api.contact.allEmailsDetail(props.contact._id, {
          headers: { Authorization: "Bearer " + token },
        })
      ).data.data;
      if (!newEmails) return;
      const alteredEmails = newEmails.map((e) => {
        return {
          _id: e._id,
          body: e.body,
          aliasId: e.aliasId,
          subject: e.subject,
          scheduleTo: e.scheduleTo ? e.scheduleTo : e.createdAt, //Show the date when was sent
          type: TYPE_MAP[e.type],
          status: e.status as IContactEmailStatus,
          isPersonal: e.isPersonal,
        };
      });
      const sortedEmails = alteredEmails.sort((a, b) => {
        return dayjs(b.scheduleTo).diff(a.scheduleTo);
      });

      setEmails(sortedEmails);

      if (sortedEmails.length < 1) {
        setIsEmpty(true);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    setFilteredEmails(getFilteredEmails());
  }, [filter, emails, infoFilter]);

  function getFilteredEmails() {
    if (!emails) return [];

    let emailsWithFilter: EmailToContact[] = [...emails];

    if (filter.type.length > 0) {
      emailsWithFilter = emailsWithFilter.filter((not) => {
        if (filter.type.find((t) => t === not.type)) return true;
        return false;
      });
    }

    if (filter.status.length > 0) {
      emailsWithFilter = emailsWithFilter.filter((not) => {
        if (filter.status.find((c) => c === not.status)) return true;
        return false;
      });
    }

    return emailsWithFilter
      .filter((not) =>
        not.subject
          .trim()
          .toLowerCase()
          .includes(infoFilter.toLowerCase().trim())
      )
      .sort((a, b) => {
        if (filter.date === DateFilter.Recent)
          return dayjs(b.scheduleTo).diff(a.scheduleTo);
        else return dayjs(a.scheduleTo).diff(b.scheduleTo);
      });
  }

  function handlePage(vle: number) {
    setPage(vle);
  }

  if (loading)
    return (
      <NotesContainer>
        <Between>
          <SubTitle css={noteTitle}>Emails</SubTitle>

          <Buttons
            css={css`
              justify-content: flex-end;
            `}
          >
            <SearchInput
              css={css`
                margin-right: 15px;
                width: 260px;
              `}
              placeholder="Search for emails"
              value={infoFilter}
              handleChange={(vle) => setInfoFilter(vle)}
            />
            <FilterEmails
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            />
          </Buttons>
        </Between>

        {Array(5)
          .fill(0)
          .map((e) => (
            <SkeletonCard
              css={css`
                width: 100%;
                height: 80px;
                margin-bottom: 10px;
              `}
            />
          ))}
      </NotesContainer>
    );

  if (isEmpty) {
    return getEmptyContainer();
  }

  function getEmptyContainer() {
    let textToShow: string;

    if (infoFilter) {
      textToShow = "No emails matching your criteria";
    } else {
      if (filter.type.length) {
        textToShow = "No emails matching your criteria";
      } else {
        textToShow = "No email to show";
      }
    }

    return (
      <EmptyContainer>
        <IconBerserkerMail name="email-empty" />
        <SubTitle
          css={css`
            margin-top: 15.8px;
          `}
        >
          {textToShow}
        </SubTitle>
      </EmptyContainer>
    );
  }

  return (
    <NotesContainer>
      {(filteredEmails.length > 0 || infoFilter || filter) && (
        <Between>
          <SubTitle css={noteTitle}>Emails</SubTitle>

          <Buttons
            css={css`
              justify-content: flex-end;
            `}
          >
            <SearchInput
              css={css`
                margin-right: 15px;
                width: 260px;
              `}
              placeholder="Search for emails"
              value={infoFilter}
              handleChange={(vle) => setInfoFilter(vle)}
            />
            <FilterEmails
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            />
          </Buttons>
        </Between>
      )}

      {filteredEmails.length > 0 ? (
        <>
          {filteredEmails
            .slice(
              page * (cachedRowsPerPage || rowsPerPage),
              page * (cachedRowsPerPage || rowsPerPage) +
                (cachedRowsPerPage || rowsPerPage)
            )
            .map((n: EmailToContact, index) => {
              return <EmailRow email={n} key={n._id} />;
            })}

          <TableFooter
            isSmaller
            page={page}
            length={filteredEmails.length || 0}
            rowsPerPage={cachedRowsPerPage || rowsPerPage}
            setRowsPerPage={(vle: number) => {
              setRowsPerPage(vle);
              updateCachedRowsPerPage(vle);
            }}
            handlePage={handlePage}
          />
        </>
      ) : (
        getEmptyContainer()
      )}
    </NotesContainer>
  );
}

function EmailRow(props: { email: EmailToContact }) {
  const modal = useModal();
  const { email } = props;
  const user = useRecoilValue(UserAtom);
  const internalUser = useRecoilValue(InternalUserAtom);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [showMore, setShowMore] = useState(true);
  const [isLarge, setIsLarge] = useState(false);
  const recipient = useRecoilValue(RecipientAtom);

  const { defaultAlias } = useDefaultAlias({
    user,
    internalUser,
  });

  function updateComponenent() {
    const subject = document.getElementById(
      "ellipse-component" + email._id + email.subject
    );
    const elem = document.createElement("div");

    if (!elem || !subject) return;

    elem.style.fontSize = "16px";
    elem.style.display = "inline";
    elem.style.width = "auto";
    elem.style.visibility = "hidden";

    elem.innerHTML = email.subject.replace(/\s\s+/g, " ").trim();

    document.body.appendChild(elem);

    setIsLarge(
      elem?.getBoundingClientRect().width >
        subject?.getBoundingClientRect().width
    );

    document.body.removeChild(elem);
  }

  useEffect(() => {
    updateComponenent();
  }, [email]);

  useEffect(() => {
    window.addEventListener("resize", updateComponenent);
    return () => window.removeEventListener("resize", updateComponenent);
  }, []);

  function getIcon() {
    switch (email.status) {
      case IContactEmailStatus.CLICKED:
        return <FaMousePointer size={16} color="#75b85c" />;

      case IContactEmailStatus.OPENED:
        return <FaRegEnvelopeOpen size={16} color="#fb972e" />;

      case IContactEmailStatus.COMPLAINED:
        return <MdReport size={16} color="#c41700" />;

      case IContactEmailStatus.UNOPENED:
        return <FaRegEnvelope size={16} color="#333333" />;
    }
  }

  return (
    <NoteRow key={"note-row" + email._id}>
      <Row>
        <Circle className={ucfirst(email.status)}>{getIcon()}</Circle>
        <Column>
          <Text className="title">
            {dayjs(email?.scheduleTo).format("MMM D, YYYY [@] h:mm a (z)")}
          </Text>
          <SubTitle>
            {EMAIL_TYPE_NAME[email.isPersonal ? "PERSONAL" : email.type]}
          </SubTitle>
        </Column>
      </Row>

      <Row>
        <Column>
          <Text className="title">Status</Text>
          <SubTitle className={ucfirst(email.status)}>
            {email.status === IContactEmailStatus.COMPLAINED
              ? "Reported Spam"
              : ucfirst(email.status)}
          </SubTitle>
        </Column>
      </Row>

      <Row
        css={css`
          overflow: hidden;
          display: block;
        `}
      >
        <Column>
          <Text className="title info">Email Subject</Text>
          <Flex>
            <Subject
              ref={rowRef}
              isEllipsis={showMore}
              css={css`
                &:hover {
                  cursor: pointer;
                }
              `}
              onClick={() => {
                modal.pushModal(
                  <PreviewEmail
                    subject={email.subject as string}
                    body={email.body as string}
                    user={user}
                    emailInfo={{
                      footer: recipient.footerId
                        ? user.emailOptions.footers.find(
                            (f) => f.id === recipient.footerId
                          )
                        : user.emailOptions.footers[0],
                      alias: email?.aliasId
                        ? user.emailOptions.aliases.find(
                            (a) => a.id === email?.aliasId
                          )
                        : defaultAlias,
                    }}
                  />,

                  <PreviewModalTitle>Preview</PreviewModalTitle>
                );
              }}
              id={"ellipse-component" + email._id + email.subject}
            >
              {email.subject}
              {isLarge && !showMore && (
                <ButtonShow
                  onClick={(e) => {
                    setShowMore(!showMore);
                    e.stopPropagation();
                  }}
                >
                  {"Show less"}
                </ButtonShow>
              )}
            </Subject>
            {isLarge && showMore && (
              <ButtonShow
                onClick={() => {
                  setShowMore(!showMore);
                }}
              >
                {"Show more"}
              </ButtonShow>
            )}
          </Flex>
        </Column>
      </Row>
    </NoteRow>
  );
}
